/**
 * Amending a City Cluster or a Zone from the geography console (ADR-0062, M11-1).
 *
 * ## Whole rows, not patches
 *
 * Each body carries every editable field, as the service-class and policy bodies do. The service
 * compares it with the stored row and records which fields moved; a request that moves nothing is
 * refused rather than written, because an event claiming an amendment that did not happen is a
 * record nothing can correct (I3).
 *
 * ## Names in both scripts are required, French is not
 *
 * `pnpm load:geometry` refuses a Zone or a Cluster without an Arabic **and** an English name, and
 * this schema holds the same rule: an Arabic-first product (§8) shows `name_ar` on the Ops board,
 * and the English name is what a Latin-script operator and the loader's own reports read. French
 * is nullable because nothing has ever written it; a blank on the screen is sent as `null`, never
 * as an empty string that would read as a name.
 *
 * ## A Zone names its Cluster by code
 *
 * An operator moves a Zone by choosing a Cluster, and a code is what the loader's files and the
 * console's lists both show. The service resolves it; an unknown one is refused with a path, not
 * turned into a new Cluster — Clusters arrive with a boundary file, never as a side effect.
 *
 * Zones carry no owner or review date because the table has neither column. Migration 0007's
 * header says otherwise, and ADR-0062 records the correction.
 */
import { z } from 'zod';
import { renderedText } from './rendered-text.js';
export const REFERENCE_STATUSES = ['active', 'retired'];
const name = z.string().trim().min(1).max(200);
const note = z.string().trim().min(1).max(500);
const reviewDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format');
// ADR-0072 exemption reasons shared by the write-body declarations below.
const REVIEW_DATE_EXEMPT = 'a YYYY-MM-DD date (review_date_format), which holds no phone-shaped digit run';
const noteExempt = (table) => `stored only in the config.${table}.amended event payload; no view renders it`;
/**
 * An IANA zone name, checked against the platform's own tz database (ADR-0065).
 *
 * **Never an offset.** Lebanon changed its DST rule by decree in March 2023 with two days of
 * notice and reversed it days later; an offset stored anywhere would have been wrong for that
 * whole period and would have read as configured. A zone name lets the tz database carry it.
 *
 * `Intl.DateTimeFormat` throws `RangeError` on a zone it does not know, which is most of the
 * check — no dependency, and no hand-kept list that goes stale the next time a government
 * renames a zone. Both markets are already in it: `Asia/Beirut` and `Asia/Damascus`.
 *
 * **On its own it is not enough, and finding that out is why this function exists.** Modern
 * engines accept a numeric offset as a `timeZone`: `new Intl.DateTimeFormat('en', { timeZone:
 * '+02:00' })` does not throw (measured on Node 25). A check built on the throw alone would
 * therefore have admitted the one thing this column exists to refuse, while looking exactly
 * like it was refusing it.
 *
 * So the shape is required too — `Area/Location`, a letter first — which also refuses:
 *
 * - `UTC`, a real IANA name and always a placeholder here. `null` is how a Cluster says it has
 *   no local calendar, and it says so without pretending the working day is in London.
 * - the `Etc/` area, whose `Etc/GMT+3` is an offset in disguise and carries the **inverted**
 *   sign by POSIX convention — the most confidently wrong value available.
 */
export function isIanaTimeZone(value) {
    if (!/^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)+$/u.test(value))
        return false;
    if (value.startsWith('Etc/'))
        return false;
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value });
        return true;
    }
    catch {
        return false;
    }
}
/** Nullable: a Cluster with no zone configures no local calendar, which shortens no promise. */
export const timezone = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .refine(isIanaTimeZone, 'timezone_unknown')
    .nullable();
export const amendClusterSchema = z
    .object({
    nameAr: name,
    nameEn: name,
    nameFr: name.nullable(),
    status: z.enum(REFERENCE_STATUSES),
    owner: z.string().trim().min(1).max(200),
    reviewDate,
    timezone,
    note,
})
    .strict();
export const amendClusterRendered = renderedText(amendClusterSchema, {
    prose: ['nameAr', 'nameEn', 'nameFr', 'owner'],
    exempt: {
        reviewDate: REVIEW_DATE_EXEMPT,
        timezone: 'an IANA zone name that isIanaTimeZone checks against the tz database; it holds no free text',
        note: noteExempt('city_clusters'),
    },
});
export const amendZoneSchema = z
    .object({
    nameAr: name,
    nameEn: name,
    nameFr: name.nullable(),
    status: z.enum(REFERENCE_STATUSES),
    cityClusterCode: z.string().trim().min(1).max(64),
    note,
})
    .strict();
export const amendZoneRendered = renderedText(amendZoneSchema, {
    prose: ['nameAr', 'nameEn', 'nameFr'],
    exempt: {
        cityClusterCode: 'must name an existing City Cluster: GeographyService resolves it and refuses an unknown one ' +
            '(unknown_city_cluster), and the Zone list renders the stored code',
        note: noteExempt('zones'),
    },
});
/**
 * A place name added to a Zone (ADR-0062, M11-2).
 *
 * `display` is the name as a caller would send it; the service normalises it with the kernel's
 * `normalizeLocality`, the same fold the gazetteer matches on, so what is stored is what resolves.
 * Owner and review date are required because the table carries both (§7).
 */
export const addLocalitySchema = z
    .object({
    display: z.string().trim().min(1).max(200),
    owner: z.string().trim().min(1).max(200),
    reviewDate,
    note,
})
    .strict();
export const addLocalityRendered = renderedText(addLocalitySchema, {
    // The place-name list renders `display` as typed and as normalizeLocality folds it (NFKC, tashkeel
    // stripped). The pipe checks the value as typed; the service checks the folded one too, because
    // the fold can turn full-width digits into ASCII ones (ADR-0072).
    prose: ['display', 'owner'],
    exempt: { reviewDate: REVIEW_DATE_EXEMPT, note: noteExempt('zone_localities') },
});
/** A place name moved to the Zone it actually names. */
export const moveLocalitySchema = z
    .object({
    zoneCode: z.string().trim().min(1).max(64),
    note,
})
    .strict();
export const moveLocalityRendered = renderedText(moveLocalitySchema, {
    exempt: {
        zoneCode: 'must name an existing Zone: GeographyLocalitiesService resolves it and refuses an unknown one ' +
            '(unknown_zone), and the list renders the stored code',
        note: noteExempt('zone_localities'),
    },
});
/** A place name removed. The note is required: the event is the only record the name existed. */
export const removeLocalitySchema = z.object({ note }).strict();
export const removeLocalityRendered = renderedText(removeLocalitySchema, {
    exempt: { note: noteExempt('zone_localities') },
});
/**
 * Whether a string is a day that exists — the shape *and* the arithmetic (ADR-0065).
 *
 * `2027-02-30` passes any regex written for `YYYY-MM-DD`. Postgres would refuse it with
 * `date/time field value out of range`, naming neither the holiday nor the field, and
 * `pnpm load:reference` would have written every earlier section first. Stated here rather than
 * in the loader for `isIanaTimeZone`'s reason: the loader and the console refuse the same day.
 *
 * A **date, never an instant.** A holiday is a day in a country's own calendar, and the moment it
 * starts depends on the City Cluster's timezone — which is why this admits no time part at all.
 */
export function isCalendarDay(value) {
    if (!/^\d{4}-\d{2}-\d{2}$/u.test(value))
        return false;
    const parsed = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === value;
}
/**
 * A public holiday declared for a country (ADR-0065, M15-2).
 *
 * **The country and the day are the identity** — the id is a digest of the pair — so they are
 * here and absent from `amendHolidaySchema`: a holiday on the wrong date is withdrawn and the
 * right one declared, rather than edited into a row whose id no longer describes it. Migration
 * 0038 grants UPDATE on neither column, so the schema and the grant say the same thing twice.
 *
 * The Arabic name is required and the other two are not, as everywhere else in this file: §8
 * makes Arabic the default, and nothing has ever written a French name for reference data.
 */
export const declareHolidaySchema = z
    .object({
    country: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z]{2}$/u, 'country_format'),
    observedOn: z.string().trim().refine(isCalendarDay, 'holiday_day_unknown'),
    nameAr: name,
    nameEn: name.nullable(),
    nameFr: name.nullable(),
    owner: z.string().trim().min(1).max(200),
    reviewDate,
    note,
})
    .strict();
export const declareHolidayRendered = renderedText(declareHolidaySchema, {
    prose: ['nameAr', 'nameEn', 'nameFr', 'owner'],
    exempt: {
        country: 'two capital letters (country_format), which hold no digit',
        observedOn: 'a calendar day (isCalendarDay, YYYY-MM-DD), which holds no phone-shaped digit run',
        reviewDate: REVIEW_DATE_EXEMPT,
        note: noteExempt('public_holidays'),
    },
});
/**
 * A declared holiday corrected, withdrawn, or observed again.
 *
 * A whole row like every other body here, carrying exactly the columns migration 0038 lets
 * `tard_app` UPDATE. **`withdrawn` is a status and not a delete**: both governments announce and
 * occasionally revoke holidays by decree, and a promised date computed while the day was observed
 * has to stay explicable afterwards.
 */
export const amendHolidaySchema = z
    .object({
    nameAr: name,
    nameEn: name.nullable(),
    nameFr: name.nullable(),
    owner: z.string().trim().min(1).max(200),
    reviewDate,
    status: z.enum(['observed', 'withdrawn']),
    note,
})
    .strict();
export const amendHolidayRendered = renderedText(amendHolidaySchema, {
    prose: ['nameAr', 'nameEn', 'nameFr', 'owner'],
    exempt: { reviewDate: REVIEW_DATE_EXEMPT, note: noteExempt('public_holidays') },
});
/**
 * A boundary file previewed or published (ADR-0062, M11-3).
 *
 * **The file itself is not parsed here.** It is a GeoJSON FeatureCollection, and the loader's own
 * `validateGeometryFile` is what refuses a bad one — every property, every polygon, every overlap —
 * for `pnpm load:geometry` and for this route alike. A second shape stated in zod would be a second
 * validator, and two validators that disagree accept a file one of them should have refused. So the
 * schema admits any `file` and the service hands it to the loader, which answers with `{ path, code }`.
 *
 * **Both flags are required, never defaulted.** `supersede` closes the previous version of every Zone
 * the file names; `retireAbsent` retires the active Zones in the file's countries that it does not
 * name, and releases their place names. A default would make either a decision nobody took.
 */
const boundaryVersion = z.string().trim().min(1).max(64);
export const previewBoundariesSchema = z
    .object({
    version: boundaryVersion,
    file: z.unknown(),
})
    .strict();
/** A boundary file's feature properties, for naming the ones a declaration renders. */
const FEATURE = 'file.features.*.properties';
const GEOMETRY = 'file.features.*.geometry';
// `file` is opaque: the properties a response or a list renders are named, and the rest is exempt.
// A feature's `geometry` object is echoed whole (as `polygon`) and stored whole by the loader, and
// assertPolygon checks only its type and its rings, so any extra member it carries is rendered.
export const previewBoundariesRendered = renderedText(previewBoundariesSchema, {
    prose: [`${FEATURE}.zone_name_ar`],
    refs: ['version', `${FEATURE}.zone_code`, `${FEATURE}.cluster_code`, GEOMETRY],
    exempt: {
        file: 'the rest of the GeoJSON: the preview stores nothing and echoes no other string of it but ' +
            'country, which validateGeometryFile limits to LB or SY',
    },
});
export const publishBoundariesSchema = z
    .object({
    version: boundaryVersion,
    file: z.unknown(),
    supersede: z.boolean(),
    retireAbsent: z.boolean(),
    owner: z.string().trim().min(1).max(200),
    reviewDate,
    note,
})
    .strict();
export const publishBoundariesRendered = renderedText(publishBoundariesSchema, {
    prose: [
        `${FEATURE}.zone_name_ar`,
        `${FEATURE}.zone_name_en`,
        `${FEATURE}.cluster_name_ar`,
        `${FEATURE}.cluster_name_en`,
        `${FEATURE}.localities`,
        'owner',
    ],
    refs: ['version', `${FEATURE}.zone_code`, `${FEATURE}.cluster_code`, GEOMETRY],
    exempt: {
        file: 'the rest of the GeoJSON: type tags, country (LB or SY) and cluster_timezone (isIanaTimeZone), ' +
            'both checked by validateGeometryFile; the loader stores no other property',
        reviewDate: REVIEW_DATE_EXEMPT,
        note: noteExempt('zone_geometries'),
    },
});
//# sourceMappingURL=geography.js.map